import * as React from 'react'
import { useFlowium } from '../client/FlowiumContext'
import Modal from './Modal'
import Input from './Input'
import Button from './Button'

interface CreateProjectModalProps {
    isOpen: boolean
    onClose: () => void
}

const CreateProjectModal: React.FC<CreateProjectModalProps> = ({ isOpen, onClose }) => {
    const [name, setName] = React.useState('')
    const [isLoading, setIsLoading] = React.useState(false)
    const [error, setError] = React.useState<string | null>(null)
    const { client, user, currentWorkspace, fetchUserData } = useFlowium()

    React.useEffect(() => {
        if (isOpen) { setName(''); setError(null) }
    }, [isOpen])

    const handleSubmit = async () => {
        const workspaceId = currentWorkspace?.id || user?.workspace
        const trimmedName = name.trim()
        if (!trimmedName || !workspaceId) return
        setIsLoading(true)
        setError(null)
        try {
            const projectId = await client.createProject(trimmedName, workspaceId)
            await client.setProject(projectId, workspaceId)
            await fetchUserData()
            onClose()
        } catch (err) {
            setError('Failed to create project')
        } finally {
            setIsLoading(false)
        }
    }

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title="Create New Project"
            description="Enter a name for your new project."
            size="sm"
            footer={
                <div className="flex justify-end gap-2">
                    <Button variant="ghost" onClick={onClose} disabled={isLoading}>
                        Cancel
                    </Button>
                    <Button onClick={handleSubmit} disabled={isLoading || !name.trim()}>
                        {isLoading ? 'Creating...' : 'Create Project'}
                    </Button>
                </div>
            }
        >
            {error && <p className="text-error text-sm mb-2">{error}</p>}
            <Input
                label="Project name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="My Awesome Project"
                disabled={isLoading}
            />
        </Modal>
    )
}

export default CreateProjectModal
